import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { InvoiceFormData } from "./types";
import { computeInvoice, fmt, numberToWords } from "../../utils/invoiceCalculations";

const B = "#000000";
const bw = 0.75;

const s = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    paddingTop: 18,
    paddingBottom: 18,
    paddingRight: 14,
    paddingLeft: 68, // generous filing & punch-hole margin (approx 24mm / 1 inch)
    fontFamily: "Helvetica",
    fontSize: 7.5,
  },
  outerBox: {
    border: bw,
    borderColor: B,
    flexDirection: "column",
  },

  /* ─── HEADER ─────────────────────────────────────────────── */
  headerWrap: {
    borderBottomWidth: bw,
    borderColor: B,
  },
  /* Top GSTIN bar */
  gstinBar: {
    borderBottomWidth: 0.4,
    borderColor: B,
    padding: "3 6",
  },
  gstinText: { fontSize: 7, fontFamily: "Helvetica-Bold" },

  /* Logo + Company row */
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoCell: {
    width: "28%",
    padding: "8 8",
    borderRightWidth: bw,
    borderColor: B,
    justifyContent: "center",
    alignItems: "center",
  },
  logo: { width: 100, height: 42, objectFit: "contain" },
  companyCell: {
    flex: 1,
    alignItems: "center",
    padding: "6 6",
  },
  taxLabel: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    textDecoration: "underline",
    marginBottom: 2,
  },
  companyName: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2.5,
    textAlign: "center",
  },
  headerSm: { fontSize: 6.8, textAlign: "center", marginBottom: 1.5 },

  /* ─── INVOICE + DISPATCH BOX ─────────────────────────────── */
  invoiceBox: {
    flexDirection: "row",
    borderBottomWidth: bw,
    borderColor: B,
  },
  invLeft: {
    width: "50%",
    borderRightWidth: bw,
    borderColor: B,
    padding: "6 8",
  },
  invRight: {
    flex: 1,
    padding: "6 8",
  },
  kvRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  kvLabel: {
    width: "42%",
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
  },
  kvColon: { width: "5%", fontSize: 7.5 },
  kvValue: { flex: 1, fontSize: 7.5 },

  /* ─── CUSTOMER BOX ───────────────────────────────────────── */
  customerBox: {
    flexDirection: "row",
    borderBottomWidth: bw,
    borderColor: B,
  },
  custLeft: {
    width: "50%",
    borderRightWidth: bw,
    borderColor: B,
    padding: "6 8",
  },
  custRight: {
    flex: 1,
    padding: "6 8",
  },
  custTitle: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    marginBottom: 3,
    textDecoration: "underline",
  },
  custLine: { fontSize: 7, marginBottom: 2 },
  custBold: { fontSize: 7.5, fontFamily: "Helvetica-Bold", marginBottom: 2.5 },
  custKvRow: { flexDirection: "row", marginBottom: 2 },
  custKvLabel: { width: "30%", fontSize: 7, fontFamily: "Helvetica-Bold" },
  custKvColon: { width: "5%", fontSize: 7 },
  custKvValue: { flex: 1, fontSize: 7 },

  /* ─── NARRATION ROW ──────────────────────────────────────── */
  narration: {
    padding: "5 8",
    borderBottomWidth: bw,
    borderColor: B,
    flexDirection: "row",
  },

  /* ─── ITEMS TABLE ────────────────────────────────────────── */
  tableHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: bw,
    borderColor: B,
    backgroundColor: "#f5f5f5",
    minHeight: 20,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.4,
    borderColor: "#aaaaaa",
    minHeight: 24,
  },
  tableRowNoBottom: {
    flexDirection: "row",
    minHeight: 20,
  },
  grandRow: {
    flexDirection: "row",
    borderTopWidth: bw,
    borderBottomWidth: bw,
    borderColor: B,
    backgroundColor: "#eeeeee",
    minHeight: 20,
  },

  /* Column widths */
  cSN: { width: "5%", padding: "4 2", borderRightWidth: bw, borderColor: B },
  cDesc: { width: "32%", padding: "4 4", borderRightWidth: bw, borderColor: B },
  cHSN: { width: "11%", padding: "4 2", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  cGST: { width: "7%", padding: "4 2", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  cQty: { width: "6%", padding: "4 2", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  cUnit: { width: "7%", padding: "4 2", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  cPrice: { width: "16%", padding: "4 3", borderRightWidth: bw, borderColor: B, textAlign: "right" },
  cAmt: { width: "16%", padding: "4 3", textAlign: "right" },

  bold: { fontFamily: "Helvetica-Bold" },
  center: { textAlign: "center" },
  right: { textAlign: "right" },

  /* ─── HSN/SAC SUMMARY ────────────────────────────────────── */
  taxSection: {
    borderTopWidth: bw,
    borderColor: B,
  },
  taxH1Row: {
    flexDirection: "row",
    borderBottomWidth: 0.4,
    borderColor: B,
    backgroundColor: "#f5f5f5",
    minHeight: 22,
  },
  taxH2Row: {
    flexDirection: "row",
    borderBottomWidth: 0.4,
    borderColor: "#aaa",
    backgroundColor: "#fafafa",
    minHeight: 16,
  },
  taxDataRow: {
    flexDirection: "row",
    borderBottomWidth: 0.4,
    borderColor: "#aaa",
    minHeight: 18,
  },
  taxTotalRow: {
    flexDirection: "row",
    borderTopWidth: bw,
    borderColor: B,
    backgroundColor: "#eeeeee",
    minHeight: 18,
  },

  txHSN: { width: "12%", padding: "3 4", borderRightWidth: bw, borderColor: B },
  txTaxVal: { width: "15%", padding: "3 4", borderRightWidth: bw, borderColor: B, textAlign: "right" },
  txCGSTH: { width: "24%", padding: "3 4", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  txSGSTH: { width: "24%", padding: "3 4", borderRightWidth: bw, borderColor: B, textAlign: "center" },
  txTotH: { flex: 1, padding: "3 4", textAlign: "right" },

  txRate: { width: "12%", padding: "3 4", borderRightWidth: 0.4, borderColor: "#aaa", textAlign: "center" },
  txAmt: { width: "12%", padding: "3 4", borderRightWidth: bw, borderColor: B, textAlign: "right" },

  /* ─── AMOUNT IN WORDS + BANK ─────────────────────────────── */
  amtBankRow: {
    flexDirection: "row",
    borderTopWidth: bw,
    borderColor: B,
  },
  amtCell: {
    width: "55%",
    padding: "6 8",
    borderRightWidth: bw,
    borderColor: B,
  },
  bankCell: {
    flex: 1,
    padding: "6 8",
  },

  /* ─── FOOTER ─────────────────────────────────────────────── */
  footer: {
    flexDirection: "row",
    borderTopWidth: bw,
    borderColor: B,
  },
  footerLeft: {
    flex: 1,
    borderRightWidth: bw,
    borderColor: B,
    padding: "8 8",
  },
  footerRight: {
    width: "35%",
    padding: "8 8",
  },
  footerLine: { fontSize: 7, marginBottom: 3 },
});

interface Props {
  data: InvoiceFormData;
}

const InvoicePDF: React.FC<Props> = ({ data }) => {
  const c = computeInvoice(Number(data.totalGrandAmount) || 0);
  const amountWords = numberToWords(c.grandTotal);

  return (
    <Document title={`Invoice-${data.invoiceNo}`}>
      <Page size="A4" orientation="portrait" style={s.page}>
        <View style={s.outerBox}>

          {/* ══ HEADER ══════════════════════════════════════════════ */}
          <View style={s.headerWrap}>
            {/* GSTIN top bar */}
            <View style={s.gstinBar}>
              <Text style={s.gstinText}>GSTIN: 32BQSPR4087B4ZJ</Text>
            </View>
            {/* Logo + Company Info */}
            <View style={s.headerRow}>
              <View style={s.logoCell}>
                <Image
                  style={s.logo}
                  src={`${process.env.PUBLIC_URL || ""}/xplor-logo.png`}
                />
              </View>
              <View style={s.companyCell}>
                <Text style={s.taxLabel}>TAX INVOICE</Text>
                <Text style={s.companyName}>XPLOR INFRASTRUCTURES</Text>
                <Text style={s.headerSm}>Virakodiyaniyil Building, Chamanpathal P O, Vazhoor, Kottayam, Kerala, 686517</Text>
                <Text style={s.headerSm}>Mobile: 9846082271</Text>
                <Text style={s.headerSm}>GSTIN/UIN: 32BQSPR4087B4ZJ  |  State Name : Kerala, Code : 32</Text>
                <Text style={s.headerSm}>E-Mail : xplorinfras@gmail.com</Text>
                <Text style={s.headerSm}>Pan: BQSPR4087B</Text>
              </View>
            </View>
          </View>

          {/* ══ INVOICE DETAILS + DISPATCH ══════════════════════════ */}
          <View style={s.invoiceBox}>
            <View style={s.invLeft}>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Invoice No</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.invoiceNo}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Date</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.invoiceDate}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Place of Supply</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.placeOfSupply}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>GR/RR No.</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.grRrNo || ""}</Text>
              </View>
            </View>
            <View style={s.invRight}>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Transport</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.transport || ""}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Vehicle No</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.vehicleNo || ""}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Station</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.station || ""}</Text>
              </View>
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>E-Way Bill No</Text>
                <Text style={s.kvColon}>:</Text>
                <Text style={s.kvValue}>{data.eWayBillNo || ""}</Text>
              </View>
            </View>
          </View>

          {/* ══ CUSTOMER DETAILS ════════════════════════════════════ */}
          <View style={s.customerBox}>
            {/* Billed To */}
            <View style={s.custLeft}>
              <Text style={s.custTitle}>Billed To :</Text>
              <Text style={s.custBold}>
                {data.customerName}{data.consumerNumber ? ` - ${data.consumerNumber}` : ""}
              </Text>
              <Text style={s.custLine}>{data.addressLine1}</Text>
              {data.addressLine2 ? <Text style={s.custLine}>{data.addressLine2}</Text> : null}
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>PH No.</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.phoneNumber}</Text>
              </View>
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>Part PAN</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.clientPan}</Text>
              </View>
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>GSTIN/UIN</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.gstin || ""}</Text>
              </View>
            </View>

            {/* Shipped To */}
            <View style={s.custRight}>
              <Text style={s.custTitle}>Shipped To :</Text>
              <Text style={s.custBold}>
                {data.customerName}{data.consumerNumber ? ` - ${data.consumerNumber}` : ""}
              </Text>
              <Text style={s.custLine}>{data.addressLine1}</Text>
              {data.addressLine2 ? <Text style={s.custLine}>{data.addressLine2}</Text> : null}
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>Consumer No</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.consumerNumber}</Text>
              </View>
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>PH No.</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.phoneNumber}</Text>
              </View>
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>Part PAN</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.clientPan}</Text>
              </View>
              <View style={s.custKvRow}>
                <Text style={s.custKvLabel}>GSTIN/UIN</Text>
                <Text style={s.custKvColon}>:</Text>
                <Text style={s.custKvValue}>{data.gstin || ""}</Text>
              </View>
            </View>
          </View>

          {/* ══ NARRATION ═══════════════════════════════════════════ */}
          <View style={s.narration}>
            <Text style={[s.bold, { fontSize: 7.5 }]}>Narration  :</Text>
          </View>

          {/* ══ ITEMS TABLE ═════════════════════════════════════════ */}
          {/* Header */}
          <View style={s.tableHeaderRow}>
            <Text style={[s.cSN, s.bold, s.center]}>S.N.</Text>
            <Text style={[s.cDesc, s.bold, s.center]}>Description of Goods</Text>
            <View style={[s.cHSN, { justifyContent: "center" }]}>
              <Text style={[s.bold, s.center]}>HSN/SAC</Text>
              <Text style={[s.bold, s.center]}>Code</Text>
            </View>
            <View style={[s.cGST, { justifyContent: "center" }]}>
              <Text style={[s.bold, s.center]}>GST</Text>
              <Text style={[s.bold, s.center]}>Rate %</Text>
            </View>
            <Text style={[s.cQty, s.bold, s.center]}>Qty.</Text>
            <Text style={[s.cUnit, s.bold, s.center]}>Unit</Text>
            <Text style={[s.cPrice, s.bold, s.center]}>Price</Text>
            <Text style={[s.cAmt, s.bold, s.center]}>Amount (₹)</Text>
          </View>

          {/* Row 1 – System */}
          <View style={s.tableRow}>
            <Text style={s.cSN}>1</Text>
            <Text style={s.cDesc}>{data.systemDescription}</Text>
            <Text style={s.cHSN}>85414300</Text>
            <Text style={s.cGST}>5</Text>
            <Text style={s.cQty}>1</Text>
            <Text style={s.cUnit}>Nos</Text>
            <Text style={s.cPrice}>{fmt(c.systemBase)}</Text>
            <Text style={s.cAmt}>{fmt(c.systemBase)}</Text>
          </View>

          {/* Row 2 – Service */}
          <View style={s.tableRow}>
            <Text style={s.cSN}>2</Text>
            <Text style={s.cDesc}>SERVICE AND INSTALLATION-{"\n"}(Solar PV System)</Text>
            <Text style={s.cHSN}>995461</Text>
            <Text style={s.cGST}>18</Text>
            <Text style={s.cQty}></Text>
            <Text style={s.cUnit}></Text>
            <Text style={s.cPrice}>{fmt(c.serviceBase)}</Text>
            <Text style={s.cAmt}>{fmt(c.serviceBase)}</Text>
          </View>

          {/* SGST row */}
          <View style={s.tableRow}>
            <Text style={s.cSN}></Text>
            <Text style={[s.cDesc, { paddingLeft: 6 }]}>SGST Output</Text>
            <Text style={s.cHSN}></Text>
            <Text style={s.cGST}></Text>
            <Text style={s.cQty}></Text>
            <Text style={s.cUnit}></Text>
            <Text style={s.cPrice}></Text>
            <Text style={s.cAmt}>{fmt(c.sgstOutput)}</Text>
          </View>

          {/* CGST row */}
          <View style={s.tableRowNoBottom}>
            <Text style={s.cSN}></Text>
            <Text style={[s.cDesc, { paddingLeft: 6 }]}>CGST Output</Text>
            <Text style={s.cHSN}></Text>
            <Text style={s.cGST}></Text>
            <Text style={s.cQty}></Text>
            <Text style={s.cUnit}></Text>
            <Text style={s.cPrice}></Text>
            <Text style={s.cAmt}>{fmt(c.cgstOutput)}</Text>
          </View>

          {/* Grand Total */}
          <View style={s.grandRow}>
            <Text style={[s.cSN, s.bold]}></Text>
            <Text style={[s.cDesc, s.bold]}>Grand Total</Text>
            <Text style={s.cHSN}></Text>
            <Text style={s.cGST}></Text>
            <Text style={[s.cQty, s.bold]}>1</Text>
            <Text style={[s.cUnit, s.bold]}>Units</Text>
            <Text style={s.cPrice}></Text>
            <Text style={[s.cAmt, s.bold]}>{fmt(c.grandTotal)}</Text>
          </View>

          {/* ══ HSN/SAC TAX SUMMARY ═════════════════════════════════ */}
          <View style={s.taxSection}>
            {/* Header row 1 */}
            <View style={s.taxH1Row}>
              <Text style={[s.txHSN, s.bold]}>HSN/SAC</Text>
              <Text style={[s.txTaxVal, s.bold]}>Taxable{"\n"}Value</Text>
              <Text style={[s.txCGSTH, s.bold]}>CGST</Text>
              <Text style={[s.txSGSTH, s.bold]}>SGST/UTGST</Text>
              <Text style={[s.txTotH, s.bold, s.right]}>Total{"\n"}Tax Amount</Text>
            </View>

            {/* Sub-header row 2 */}
            <View style={s.taxH2Row}>
              <Text style={s.txHSN}></Text>
              <Text style={s.txTaxVal}></Text>
              <Text style={s.txRate}>Rate</Text>
              <Text style={s.txAmt}>Amount</Text>
              <Text style={s.txRate}>Rate</Text>
              <Text style={s.txAmt}>Amount</Text>
              <Text style={s.txTotH}></Text>
            </View>

            {/* System row */}
            <View style={s.taxDataRow}>
              <Text style={s.txHSN}>85414300</Text>
              <Text style={[s.txTaxVal, s.right]}>{fmt(c.systemBase)}</Text>
              <Text style={[s.txRate, s.center]}>2.5%</Text>
              <Text style={[s.txAmt, s.right]}>{fmt(c.systemCGST)}</Text>
              <Text style={[s.txRate, s.center]}>2.5%</Text>
              <Text style={[s.txAmt, s.right]}>{fmt(c.systemSGST)}</Text>
              <Text style={[s.txTotH, s.right]}>{fmt(c.systemTotalTax)}</Text>
            </View>

            {/* Service row */}
            <View style={s.taxDataRow}>
              <Text style={s.txHSN}>995461</Text>
              <Text style={[s.txTaxVal, s.right]}>{fmt(c.serviceBase)}</Text>
              <Text style={[s.txRate, s.center]}>9%</Text>
              <Text style={[s.txAmt, s.right]}>{fmt(c.serviceCGST)}</Text>
              <Text style={[s.txRate, s.center]}>9%</Text>
              <Text style={[s.txAmt, s.right]}>{fmt(c.serviceSGST)}</Text>
              <Text style={[s.txTotH, s.right]}>{fmt(c.serviceTotalTax)}</Text>
            </View>

            {/* Total row */}
            <View style={s.taxTotalRow}>
              <Text style={[s.txHSN, s.bold]}>Total</Text>
              <Text style={[s.txTaxVal, s.bold, s.right]}>{fmt(c.totalTaxableValue)}</Text>
              <Text style={s.txRate}></Text>
              <Text style={[s.txAmt, s.bold, s.right]}>{fmt(c.totalCGST)}</Text>
              <Text style={s.txRate}></Text>
              <Text style={[s.txAmt, s.bold, s.right]}>{fmt(c.totalSGST)}</Text>
              <Text style={[s.txTotH, s.bold, s.right]}>{fmt(c.totalTax)}</Text>
            </View>
          </View>

          {/* ══ AMOUNT IN WORDS + BANK DETAILS ══════════════════════ */}
          <View style={s.amtBankRow}>
            <View style={s.amtCell}>
              <Text style={{ fontSize: 7.5, marginBottom: 2 }}>
                <Text style={s.bold}>Amount in Words:{"\n"}</Text>
              </Text>
              <Text style={{ fontSize: 8, fontFamily: "Helvetica-Bold" }}>{amountWords}</Text>
            </View>
            <View style={s.bankCell}>
              <Text style={[s.bold, { fontSize: 7.5, marginBottom: 2 }]}>Bank Details:</Text>
              <Text style={{ fontSize: 7, marginBottom: 1 }}>Bank Name: CENTRAL BANK OF INDIA</Text>
              <Text style={{ fontSize: 7, marginBottom: 1 }}>A/C No.: 5877686812    IFSC: CBIN0280957</Text>
              <Text style={{ fontSize: 7 }}>BRANCH: VAZHOOR</Text>
            </View>
          </View>

          {/* ══ FOOTER ══════════════════════════════════════════════ */}
          {/* Row 1: Receiver's Signature label */}
          <View style={{ flexDirection: "row", borderTopWidth: bw, borderColor: B }}>
            <View style={{ flex: 1, borderRightWidth: bw, borderColor: B, padding: "5 8" }}>
              <Text style={{ fontSize: 7.5 }}>Receiver's Signature :</Text>
            </View>
            <View style={{ width: "35%", padding: "5 8" }}>
              <Text style={{ fontSize: 7.5 }}> </Text>
            </View>
          </View>

          {/* Row 2: Terms & Conditions (left) + Company Signature (right) */}
          <View style={s.footer}>
            <View style={s.footerLeft}>
              <Text style={[s.bold, { fontSize: 8, marginBottom: 4 }]}>Terms &amp; Conditions</Text>
              <Text style={s.footerLine}>1  Goods once sold will not be taken back or exchanged.</Text>
              <Text style={s.footerLine}>2  Interest @ 18% p.a. will be charged for delayed payments.</Text>
              <Text style={s.footerLine}>3  Warranty for energy &amp; net meter is subject to conditions. Burning faults will not be covered{"\n"}   under warranty.</Text>
              <Text style={s.footerLine}>4  Testing fee for new meter will be charged for warranty replacements.</Text>
            </View>
            <View style={s.footerRight}>
              {/* "For Xplor Infrastructures" in blue bold */}
              <Text style={{ fontSize: 8.5, color: "#1a3ec4", fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 }}>
                For  Xplor  Infrastructures
              </Text>
              {/* "Sole proprietor" in blue italic */}
              <Text style={{ fontSize: 7.5, color: "#1a3ec4", fontFamily: "Helvetica-Oblique", textAlign: "center", marginBottom: 3 }}>
                Sole proprietor
              </Text>
              {/* Signature image on white background */}
              <View style={{ backgroundColor: "#ffffff", alignItems: "center", marginVertical: 3 }}>
                <Image
                  src={`${process.env.PUBLIC_URL || ""}/signature.png`}
                  style={{ width: 100, height: 32, objectFit: "contain" }}
                />
              </View>
              {/* Horizontal rule + label */}
              <Text style={{ fontSize: 7, borderTopWidth: 0.5, borderColor: B, paddingTop: 3, textAlign: "center" }}>
                Authorised Signature
              </Text>
            </View>
          </View>

        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
